import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { CancelOutlined, CheckCircleOutline, Edit } from '@mui/icons-material';
import { PageIndices } from '../../common/i-doc/i-doc.registry.ts';
import { PlanChip } from '@components/admin/PlanChip.tsx';
import { DashboardLayout } from '@components/DashboardLayout.tsx';
import { PageCard } from '@components/common/PageCard.tsx';
import { PageHint } from '@components/common/PageHint.tsx';
import { PageToolbar } from '@components/common/PageToolbar.tsx';
import { UniversalPagination } from '@components/common/UniversalPagination.tsx';
import { UserAvatar } from '@components/common/UserAvatar.tsx';
import { adminUsersService, AdminClientUser, AdminPlanOption } from '@services/admin-users.service.ts';
import { toastService } from '@services/toast.service.ts';
import { AdminChangePlanModal } from '@components/admin/AdminChangePlanModal.tsx';

// Дублікат AdminClientsPage.tsx, урізаний лише до білінгових полів (тариф + billingExempt).
// Живе окремо в блоці "Фінанси" — навмисно, без переносу з Users-сторінки (див. workspace/changelog).

const AdminFinanceClientsPageContent: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  const [users, setUsers] = useState<AdminClientUser[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [billingToggling, setBillingToggling] = useState<number | null>(null);

  const [planOptions, setPlanOptions] = useState<AdminPlanOption[]>([]);
  const [planModalUser, setPlanModalUser] = useState<AdminClientUser | null>(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const result = await adminUsersService.getClients({
        page,
        limit,
        search: searchQuery || undefined,
        planCode: planFilter || undefined,
      });
      setUsers(result.data);
      setTotal(result.meta.total);
    } catch {
      toastService.error('Не вдалося завантажити список клієнтів.');
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchQuery, planFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    adminUsersService.getClientPaymentsOptions().then((opts) => setPlanOptions(opts.plans));
  }, []);

  const handlePlanChange = async (planId: number) => {
    if (!planModalUser) return;
    await adminUsersService.setClientPlan(planModalUser.id, planId);
    const plan = planOptions.find((p) => p.value === planId);
    setUsers((prev) => prev.map((u) =>
      u.id === planModalUser.id
        ? { ...u, subscription: plan ? { planName: plan.label, planCode: plan.code, status: 'active' } : u.subscription }
        : u,
    ));
    toastService.success('Тарифний план змінено.');
  };

  const handleBillingToggle = async (user: AdminClientUser) => {
    setBillingToggling(user.id);
    const newValue = !user.billingExempt;
    try {
      await adminUsersService.setBillingExempt(user.id, newValue);
      setUsers((prev) => prev.map((u) => u.id === user.id ? { ...u, billingExempt: newValue } : u));
      toastService.success(newValue ? 'Білінг вимкнено для клієнта.' : 'Білінг увімкнено для клієнта.');
    } catch {
      toastService.error('Не вдалося змінити налаштування білінгу.');
    } finally {
      setBillingToggling(null);
    }
  };

  return (
    <Box data-i-doc={PageIndices.ADMIN_FINANCE_CLIENTS}>
      <PageHint iDoc={PageIndices.ADMIN_FINANCE_CLIENTS} />

      <PageCard>
        <PageToolbar
          storageKey="filters:admin-finance-clients"
          filters={[
            {
              id: 'search',
              type: 'text',
              label: 'Пошук за іменем або email',
              value: searchQuery,
              onChange: (v) => { setSearchQuery(v); setPage(1); },
              flexGrow: 1,
            },
            {
              id: 'planCode',
              type: 'select',
              label: 'Тариф',
              value: planFilter,
              onChange: (v) => { setPlanFilter(v); setPage(1); },
              options: [
                { value: '', label: 'Всі' },
                ...planOptions.map((p) => ({ value: p.code, label: p.label })),
              ],
            },
          ]}
          onReset={() => { setSearchQuery(''); setPlanFilter(''); setPage(1); }}
          showReset
        />

        {loading ? (
          <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : users.length === 0 ? (
          <Typography sx={{ mt: 4, textAlign: 'center' }}>Клієнтів не знайдено.</Typography>
        ) : (
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Аватар</TableCell>
                  <TableCell>Ім'я / Email / Телефон</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell>Тариф</TableCell>
                  <TableCell>Білінг вимкнено</TableCell>
                  <TableCell>Реєстрація</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => {
                  const isActive = user.status === 1;
                  return (
                    <TableRow key={user.id}>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{user.id}</Typography>
                      </TableCell>
                      <TableCell>
                        <UserAvatar path={user.avatar} name={user.name} sx={{ width: 32, height: 32 }} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{user.name}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                          <Typography variant="caption" color="text.secondary">{user.email}</Typography>
                          {user.isEmailVerified ? (
                            <Tooltip title="Email верифіковано">
                              <CheckCircleOutline color="success" sx={{ fontSize: 13 }} />
                            </Tooltip>
                          ) : (
                            <Tooltip title="Email не верифіковано">
                              <CancelOutlined color="disabled" sx={{ fontSize: 13 }} />
                            </Tooltip>
                          )}
                        </Box>
                        {user.phone && (
                          <Typography variant="caption" color="text.secondary">{user.phone}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={isActive ? 'Активний' : 'Заблокований'}
                          color={isActive ? 'success' : 'error'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PlanChip subscription={user.subscription} />
                          <Tooltip title="Тимчасово вимкнено — керування тарифом перенесено на сторінку «Клієнти» (Користувачі)">
                            <span>
                              <IconButton size="small" disabled>
                                <Edit fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={user.billingExempt ? 'Білінг вимкнено (exempt)' : 'Білінг увімкнено'}>
                          <span>
                            <Switch
                              checked={user.billingExempt}
                              onChange={() => handleBillingToggle(user)}
                              disabled={billingToggling === user.id}
                              size="small"
                              color="warning"
                            />
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{new Date(user.createdAt).toLocaleDateString('uk-UA')}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <UniversalPagination
          page={page}
          count={Math.ceil(total / limit)}
          limit={limit}
          total={total}
          onPageChange={setPage}
          onLimitChange={(v) => { setLimit(v); setPage(1); }}
          storageKey="pagination:admin-finance-clients"
        />
      </PageCard>

      <AdminChangePlanModal
        open={!!planModalUser}
        onClose={() => setPlanModalUser(null)}
        onSave={handlePlanChange}
        plans={planOptions}
        currentPlanCode={planModalUser?.subscription?.planCode ?? null}
        userName={planModalUser?.name ?? ''}
      />
    </Box>
  );
};

export const AdminFinanceClientsPage: React.FC = () => (
  <DashboardLayout>
    <AdminFinanceClientsPageContent />
  </DashboardLayout>
);
